import { Dispatch, FC, SetStateAction, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Buyer } from '@/app/api/types';
import { useTranslation } from 'react-i18next';
import { useLazyGetDataByTinQuery } from '@/entities/didox/api';
import FormSelectInput from '@/shared/inputs/form-select-input';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { CreateSellerRequestValues } from '../../types';

interface Props {
  form: UseFormReturn<CreateSellerRequestValues, unknown, undefined>;
  options: Buyer[];
  setOptions: Dispatch<SetStateAction<Buyer[]>>;
  isLoading: boolean;
  onChange: () => void;
  required?: boolean;
}

export const BuyerTinInput: FC<Props> = ({
  form,
  options,
  setOptions,
  isLoading,
  onChange,
  required = false,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [getDataByTin, { isLoading: isLoadingTin }] = useLazyGetDataByTinQuery();
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = async (value: string) => {
    setInputValue(value);

    // Check if input is a 9-digit TIN and not already in options
    if (value.length === 9 && /^\d+$/.test(value) && !options.some((opt) => opt.tin === value)) {
      try {
        const response = await getDataByTin({ tin: value }).unwrap();
        if (response) {
          const newBuyer: Buyer = {
            tin: value,
            name: response.name,
          };

          setOptions((prev) => [...prev, newBuyer]);
          form.setValue('buyer', newBuyer);

          showNotification(t('common:buyer_added_successfully'), 'success');
        }
      } catch (err) {
        showNotification(t('error:tin_not_found'), 'error');
        form.setValue('buyer', null);
      }
    }
  };

  return (
    <FormSelectInput
      control={form.control}
      name='buyer'
      options={options}
      getOptionLabel={(o) => `${o.name} (${o.tin})`}
      onChange={() => onChange()}
      loading={isLoading || isLoadingTin}
      freeSolo
      inputValue={inputValue}
      onInputChange={(_, value) => handleInputChange(value)}
      TextFieldProps={{
        placeholder: t('common:chose_from_list_or_fill_tin'),
      }}
      rules={{
        required,
      }}
    />
  );
};
